import React, {useState} from 'react';

import {
  WBAgentRunSelectorGroupingContextSlice,
  WBAgentWorkspaceContextSlice,
} from '../../aria/externalBindings/WBAgentContextSlices';
import {PanelBankSectionsPagingContextProvider} from '../../state/panelbank/PanelBankSectionsPagingContext';
import {PanelsSharedRefsContextProvider} from '../Views/contexts/PanelsSharedRefsContext';
import {NamedWorkspaceViewBarWrap} from '../Views/NamedWorkspaceViewBar';
import {WorkspaceDrawerContextProvider} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import * as S from './MultipleRunWorkspace.styles';
import {MultiRunWorkspaceContentContainer} from './MultiRunWorkspaceContentContainer';
import {type MultiRunWorkspaceAllProps} from './types';

export const MultiRunWorkspace: React.FC<MultiRunWorkspaceAllProps> = ({
  children,
  entityName,
  viewType,
  projectName,
  isTableExpanded,
  onSetTableExpanded,
  isSingleMode,
  onSelectRunName,
  selectedRunName,
  workspaceID,
  recommendedGrouping,
  showArtifactCounts,
  showLogCounts,
  subTitle,
  title,
  project,
  // WorkspaceSelectors.MultiRunWorkspacePageData
  sectionPart,
  panelBankConfigRef,
  nwId,

  runSetRef,
}) => {
  // isProjectReadOnly refers to the current viewer's access to the project.
  // A user with readonly access to a project can make exploratory
  // workspace changes, e.g. search/filter runs, add/edit panels, etc.
  // This is much like playing around in a teammate's personal workspace,
  // where changes are simply discarded at the end of the session.
  // However, the user CANNOT create/edit/delete project-level resources
  // like runs, sweeps, or reports.
  const isProjectReadOnly = project.readOnly;

  const [panelBankWidth, setPanelBankWidth] = useState(0);

  return (
    <PanelsSharedRefsContextProvider
      customRunColorsRef={sectionPart.customRunColorsRef}
      customRunNamesRef={sectionPart.customRunNamesRef}
      panelBankConfigRef={panelBankConfigRef}
      runSetRefs={sectionPart.runSetRefs}
      semanticLegendRef={sectionPart.semanticLegendRef}
      workspaceSettingsRef={sectionPart.workspaceSettingsRef}>
      <WorkspaceDrawerContextProvider>
        <PanelBankSectionsPagingContextProvider>
          <WBAgentWorkspaceContextSlice
            nwId={nwId}
            viewType={viewType}
            workspaceID={workspaceID}
          />
          <WBAgentRunSelectorGroupingContextSlice runSetRef={runSetRef} />
          <NamedWorkspaceViewBarWrap
            workspaceStateId={workspaceID}
            viewType={viewType}
            isProjectReadOnly={isProjectReadOnly}
          />
          <S.MultipleRunWorkspace data-test="multiple-run-workspace">
            <MultiRunWorkspaceContentContainer
              entityName={entityName}
              isProjectReadOnly={isProjectReadOnly}
              isSingleMode={!!isSingleMode}
              isTableExpanded={isTableExpanded}
              onSelectRunName={onSelectRunName}
              onSetTableExpanded={onSetTableExpanded}
              panelBankWidth={panelBankWidth}
              projectName={projectName}
              recommendedGrouping={recommendedGrouping}
              runSetRef={runSetRef}
              sectionPart={sectionPart}
              selectedRunName={selectedRunName}
              setPanelBankWidth={setPanelBankWidth}
              showArtifactCounts={!!showArtifactCounts}
              showLogCounts={!!showLogCounts}
              subTitle={subTitle}
              title={title}
              workspaceID={workspaceID}>
              {children}
            </MultiRunWorkspaceContentContainer>
          </S.MultipleRunWorkspace>
        </PanelBankSectionsPagingContextProvider>
      </WorkspaceDrawerContextProvider>
    </PanelsSharedRefsContextProvider>
  );
};
